/**
 * OnboardingProvider Component
 * Context provider for global onboarding state management
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { OnboardingContextValue, OnboardingTour } from '@/types/onboarding';
import { useOnboarding } from '@/hooks/useOnboarding';
import { allTours, getTourById } from '@/config/onboardingSteps';

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

/**
 * Hook to access onboarding context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: React.ReactNode;
}

/**
 * OnboardingProvider Component
 */
export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const onboardingHook = useOnboarding();
  const [currentTour, setCurrentTour] = useState<OnboardingTour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  /**
   * Start a specific tour
   */
  const startTour = useCallback((tourId: string) => {
    const tour = getTourById(tourId);
    if (!tour) {
      console.error(`Tour not found: ${tourId}`);
      return;
    }

    setCurrentTour(tour);
    setCurrentStepIndex(0);
    setIsActive(true);
    setIsPaused(false);
  }, []);

  /**
   * Auto-start tour for new users
   */
  useEffect(() => {
    if (onboardingHook.shouldShowOnboarding() && !currentTour) {
      // Find the first tour that hasn't been completed or skipped
      const firstTour = allTours.find(
        (tour) =>
          !onboardingHook.isTourCompleted(tour.id) &&
          !onboardingHook.progress.skippedTours.includes(tour.id)
      );

      if (firstTour && firstTour.autoStart) {
        // Delay to ensure DOM is ready
        const timer = setTimeout(() => {
          startTour(firstTour.id);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [onboardingHook, currentTour, startTour]);

  /**
   * Skip the current or specified tour
   */
  const skipTour = useCallback(
    (tourId?: string) => {
      const idToSkip = tourId || currentTour?.id;
      if (idToSkip) {
        onboardingHook.skipTour(idToSkip);
      }

      setCurrentTour(null);
      setCurrentStepIndex(0);
      setIsActive(false);
      setIsPaused(false);
    },
    [currentTour, onboardingHook]
  );

  /**
   * Complete the current or specified tour
   */
  const completeTour = useCallback(
    (tourId?: string) => {
      const idToComplete = tourId || currentTour?.id;
      if (idToComplete) {
        onboardingHook.completeTour(idToComplete);
      }

      setCurrentTour(null);
      setCurrentStepIndex(0);
      setIsActive(false);
      setIsPaused(false);
    },
    [currentTour, onboardingHook]
  );

  /**
   * Pause the current tour
   */
  const pauseTour = useCallback(() => {
    setIsPaused(true);
    setIsActive(false);
  }, []);

  /**
   * Resume a paused tour
   */
  const resumeTour = useCallback(() => {
    if (currentTour) {
      setIsPaused(false);
      setIsActive(true);
    }
  }, [currentTour]);

  /**
   * Navigate to next step
   */
  const nextStep = useCallback(() => {
    if (!currentTour) return;

    // Mark current step as completed
    const currentStep = currentTour.steps[currentStepIndex];
    if (currentStep) {
      onboardingHook.completeStep(currentStep.id);
    }

    // Move to next step or complete tour
    if (currentStepIndex < currentTour.steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      completeTour();
    }
  }, [currentTour, currentStepIndex, onboardingHook, completeTour]);

  /**
   * Navigate to previous step
   */
  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  /**
   * Jump to a specific step
   */
  const goToStep = useCallback(
    (stepIndex: number) => {
      if (currentTour && stepIndex >= 0 && stepIndex < currentTour.steps.length) {
        setCurrentStepIndex(stepIndex);
      }
    },
    [currentTour]
  );

  /**
   * Complete a specific step
   */
  const completeStep = useCallback(
    (stepId: string) => {
      onboardingHook.completeStep(stepId);
    },
    [onboardingHook]
  );

  /**
   * Context value
   */
  const value = useMemo<OnboardingContextValue>(
    () => ({
      progress: onboardingHook.progress,
      currentTour,
      currentStepIndex,
      isActive: isActive && !isPaused,

      // Tour control
      startTour,
      skipTour,
      completeTour,
      pauseTour,
      resumeTour,

      // Step navigation
      nextStep,
      previousStep,
      goToStep,
      completeStep,

      // Checklist methods
      completeChecklistItem: onboardingHook.completeChecklistItem,
      uncompleteChecklistItem: onboardingHook.uncompleteChecklistItem,
      isChecklistItemCompleted: onboardingHook.isChecklistItemCompleted,

      // Progress methods
      resetOnboarding: onboardingHook.resetOnboarding,
      dismissWelcome: onboardingHook.dismissWelcome,
      setOnboardingComplete: onboardingHook.setOnboardingComplete,

      // State queries
      isTourCompleted: onboardingHook.isTourCompleted,
      isStepCompleted: onboardingHook.isStepCompleted,
      shouldShowOnboarding: onboardingHook.shouldShowOnboarding,
    }),
    [
      onboardingHook,
      currentTour,
      currentStepIndex,
      isActive,
      isPaused,
      startTour,
      skipTour,
      completeTour,
      pauseTour,
      resumeTour,
      nextStep,
      previousStep,
      goToStep,
      completeStep,
    ]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};
